import React, { useCallback, useMemo } from "react";
import { UI_BODY_SEMIBOLD_SM, ButtonGhostV2, ButtonTertiaryCompact, icons, colors2 } from "oolib";
import { getAnnoFilterBucketKey } from "@okf/ootils";
import { Lightbox, LightboxContent, LightboxTrigger } from "@/components/UI/lightbox";
import { StyledCompWrapper } from "./index";
import { AnnoAppliedFiltersBlocks } from "./AnnoAppliedFilters";
import { isAnnoFilter } from "./AnnoAppliedFilters/utils/groupAnnoFilters";
import { DocAppliedFiltersBlocks, isDocFilter } from "./DocAppliedFilters";
import { SaveCohortButton } from "../Cohorts/SaveCohortButton";

const { Funnel } = icons;

export function LightboxFilterHeader({
  filterConfig,
  savedFilterConfig,
  setSavedFilterConfig,
  setSavedActiveFilters,
  generateFilters,
  setActiveFilters,
  activeFilters,
  anchorEl,
  lightboxOpen,
  onLightboxOpenChange,
  hideFilterModuleHeader = false,
  addFilterButtonText = "Add Filter",
  hideIntraFilterOperatorSelect = false,
}) {
  const hideHeader = !!anchorEl;
  return (
    <div>
      {!hideHeader && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 1.4rem 0" }}>

          {!hideFilterModuleHeader && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Funnel size={16} weight="bold" color={colors2.grey80} />
              <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>Filters</UI_BODY_SEMIBOLD_SM>
            </div>
          )}

          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>

            <SaveCohortButton activeFilters={activeFilters} />

            {(activeFilters.length > 0 || savedFilterConfig?.length > 0) && (
              <ButtonTertiaryCompact
                responsive={true}
                value="Clear"
                icon="X"
                onClick={() => {
                  if (activeFilters.length) {
                    setActiveFilters([]);
                  } else {
                    setSavedFilterConfig([]);
                    setSavedActiveFilters([]);
                  }
                }}
              />
            )}

            <Lightbox multilayer={true} placement="right-start">
              <LightboxTrigger>
                <ButtonTertiaryCompact responsive={true} value={addFilterButtonText} icon="Plus" />
              </LightboxTrigger>

              <LightboxContent style={{ zIndex: 99999 }}>
                <StyledCompWrapper style={{ flexGrow: 1 }}>
                  {generateFilters(filterConfig)}
                </StyledCompWrapper>
              </LightboxContent>
            </Lightbox>

          </div>

        </div>
      )}

      {hideHeader && (
        <Lightbox
          multilayer={true}
          placement="right-start"
          {...(anchorEl ? {
            anchorEl,
            open: lightboxOpen,
            onOpenChange: onLightboxOpenChange,
          } : {})}
        >
          <LightboxContent style={{ zIndex: 99999 }}>
            <StyledCompWrapper style={{ flexGrow: 1 }}>
              {generateFilters(filterConfig)}
            </StyledCompWrapper>
          </LightboxContent>
        </Lightbox>
      )}
    </div>
  );
}

const findConfigByFilterType = (configs, filterType) => {
  if (!configs?.length) return null;
  for (const f of configs) {
    if (f?.source?.filterType === filterType) return f;
    if (f.configs) {
      const found = findConfigByFilterType(f.configs, filterType);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Collects ALL configs matching a filterType (not just the first).
 */
const findAllConfigsByFilterType = (configs, filterType) => {
  const results = [];
  if (!configs?.length) return results;
  for (const f of configs) {
    if (f?.source?.filterType === filterType) results.push(f);
    if (f.configs) results.push(...findAllConfigsByFilterType(f.configs, filterType));
  }
  return results;
};

const findAnnoHierarchyConfig = (configs) =>
  findConfigByFilterType(configs, "annoHierarchyType");

/**
 * Finds all docHierarchyType configs and merges their source arrays into
 * a single virtual config. This handles the case where metadata filters and
 * document field filters are split into separate hierarchy configs.
 */
const findDocHierarchyConfig = (configs) => {
  const all = findAllConfigsByFilterType(configs, "docHierarchyType");
  if (all.length === 0) return null;
  if (all.length === 1) return all[0];
  // Merge source arrays from all configs into one
  return {
    ...all[0],
    source: {
      ...all[0].source,
      commonFilters: all.flatMap((c) => c.source?.commonFilters || []),
      commonDocumentFilters: all.flatMap((c) => c.source?.commonDocumentFilters || []),
      sharedAcrossMultiple: all.flatMap((c) => c.source?.sharedAcrossMultiple || []),
      perDataset: all.flatMap((c) => c.source?.perDataset || []),
    },
  };
};

export function AppliedFilters({
  filterConfig,
  activeFilters,
  setActiveFilters,
  onChange,
  allFilterConfig,
  contentTypes,
  disableInlinePadding,
  hideIntraFilterOperatorSelect = false,
  setSavedFilterConfig,
  noAppliedValueChipsTruncation = false,
}) {

  // For anno sub-themes/tags blocks, IntraFilterOperatorSelect passes an
  // annoFilterBucketKey — the operator is stored on a per-bucket map. For
  // themes and doc filters, no annoFilterBucketKey is passed and the operator
  // is stored as a plain string.
  const handleIntraFilterOperatorChange = useCallback((filterId, operator, annoFilterBucketKey) => {
    setActiveFilters((prev) =>
      prev.map((f) => {
        if (f.filterId !== filterId) return f;
        if (annoFilterBucketKey != null) {
          const prevMap = f.intraFilterOperator && typeof f.intraFilterOperator === "object"
            ? f.intraFilterOperator
            : {};
          return { ...f, intraFilterOperator: { ...prevMap, [annoFilterBucketKey]: operator } };
        }
        return { ...f, intraFilterOperator: operator };
      })
    );
  }, [setActiveFilters]);

  const handleChange = useCallback(
    (id, value) => onChange({ id, value }),
    [onChange]
  );

  const handleRemoveSavedConfig = useCallback((block) => {
    if (typeof setSavedFilterConfig !== "function") return;
    setSavedFilterConfig((prev) => {
      const next = [];
      for (const f of prev) {
        if (f.filterId !== block.filterId) {
          next.push(f);
          continue;
        }
        if (block.annoFilterBucketKey != null && Array.isArray(f._savedGroups)) {
          const newGroups = f._savedGroups.filter(
            (g) => getAnnoFilterBucketKey(block.level, g) !== block.annoFilterBucketKey
          );
          if (newGroups.length > 0) next.push({ ...f, _savedGroups: newGroups });
        }
      }
      return next;
    });
  }, [setSavedFilterConfig]);

  const annoFilterConfig = useMemo(
    () => findAnnoHierarchyConfig(allFilterConfig),
    [allFilterConfig]
  );

  const docFilterConfig = useMemo(
    () => findDocHierarchyConfig(allFilterConfig),
    [allFilterConfig]
  );

  const hasActiveAnnoFilters = activeFilters?.some(
    (f) => isAnnoFilter(f, annoFilterConfig)
  );
  const hasSavedAnnoConfig = filterConfig?.some(
    (f) => isAnnoFilter(f, annoFilterConfig)
  );

  const hasActiveDocFilters = activeFilters?.some(
    (f) => isDocFilter(f, docFilterConfig)
  );

  const nonAnnoFilterConfig = useMemo(
    () => filterConfig?.filter((f) => !isAnnoFilter(f, annoFilterConfig)) ?? [],
    [filterConfig, annoFilterConfig]
  );

  // Saved annotation configs (for passing to AnnoAppliedFiltersBlocks)
  const savedAnnoConfigs = useMemo(
    () => filterConfig?.filter((f) => isAnnoFilter(f, annoFilterConfig)) ?? [],
    [filterConfig, annoFilterConfig]
  );

  // Split non-anno configs into doc configs and others
  const savedDocFilterConfigs = useMemo(
    () => nonAnnoFilterConfig.filter((f) => isDocFilter(f, docFilterConfig)),
    [nonAnnoFilterConfig, docFilterConfig]
  );

  const hasSavedDocConfig = savedDocFilterConfigs.length > 0;

  if (!hasActiveAnnoFilters && !hasSavedAnnoConfig && !hasActiveDocFilters && !hasSavedDocConfig) return null;

  return (
    <>
      {/* Annotation section: always use block UI when there are active or saved configs */}
      {(hasActiveAnnoFilters || hasSavedAnnoConfig) && annoFilterConfig ? (
        <AnnoAppliedFiltersBlocks
          activeFilters={activeFilters}
          onChange={handleChange}
          annoFilterConfig={annoFilterConfig}
          savedConfigs={savedAnnoConfigs}
          onIntraFilterOperatorChange={hideIntraFilterOperatorSelect ? undefined : handleIntraFilterOperatorChange}
          onRemoveSavedConfig={handleRemoveSavedConfig}
          contentTypes={contentTypes}
          disableInlinePadding={disableInlinePadding}
          noAppliedValueChipsTruncation={noAppliedValueChipsTruncation}
        />
      ) : null}

      {/* Document section: always use block UI when there are active or saved configs */}
      {(hasActiveDocFilters || hasSavedDocConfig) && docFilterConfig ? (
        <DocAppliedFiltersBlocks
          activeFilters={activeFilters}
          onChange={handleChange}
          docFilterConfig={docFilterConfig}
          savedConfigs={savedDocFilterConfigs}
          onIntraFilterOperatorChange={hideIntraFilterOperatorSelect ? undefined : handleIntraFilterOperatorChange}
          onRemoveSavedConfig={handleRemoveSavedConfig}
          contentTypes={contentTypes}
          disableInlinePadding={disableInlinePadding}
          noAppliedValueChipsTruncation={noAppliedValueChipsTruncation}
        />
      ) : null}

    </>
  );
}
